import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Building2, Users, CreditCard, BarChart3, Plus, Search, 
  MoreVertical, Check, X, Pause, Play, UserPlus, Eye,
  TrendingUp, Calendar, DollarSign, Crown, Shield, User,
  ChevronRight, RefreshCw, Mail, Phone, MapPin, FileText,
  ExternalLink, AlertTriangle, Clock, Key, Download, Copy, Send, Loader2, Briefcase,
  Activity, Archive, History, Database, Bell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { API } from '../utils/api';

// Componente Stats Card
const StatCard = ({ title, value, icon: Icon, trend, color = "blue" }) => {
  const colorClasses = {
    blue: "from-blue-500 to-blue-600",
    green: "from-green-500 to-green-600",
    purple: "from-purple-500 to-purple-600",
    orange: "from-orange-500 to-orange-600"
  };

  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">{title}</p>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
            {trend && (
              <p className={`text-sm mt-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend > 0 ? '+' : ''}{trend}% questo mese
              </p>
            )}
          </div>
          <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center shadow-lg`}>
            <Icon className="h-7 w-7 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Status Badge
const StatusBadge = ({ status }) => {
  const statusConfig = {
    active: { label: 'Attivo', color: 'bg-green-100 text-green-700' },
    trial: { label: 'Trial', color: 'bg-blue-100 text-blue-700' },
    suspended: { label: 'Sospeso', color: 'bg-red-100 text-red-700' },
    cancelled: { label: 'Cancellato', color: 'bg-gray-100 text-gray-700' }
  };
  const config = statusConfig[status] || statusConfig.active;
  return <Badge className={config.color}>{config.label}</Badge>;
};

// Plan Badge
const PlanBadge = ({ plan }) => {
  const planConfig = {
    free: { label: 'Free', color: 'bg-gray-100 text-gray-700', icon: null },
    starter: { label: 'Starter', color: 'bg-blue-100 text-blue-700', icon: null },
    professional: { label: 'Professional', color: 'bg-purple-100 text-purple-700', icon: Crown },
    enterprise: { label: 'Enterprise', color: 'bg-amber-100 text-amber-700', icon: Crown }
  };
  const config = planConfig[plan] || planConfig.free;
  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      {config.icon && <config.icon className="h-3 w-3" />}
      {config.label}
    </Badge>
  );
};

// Role Badge
const RoleBadge = ({ role }) => {
  const roleConfig = {
    super_admin: { label: 'Super Admin', color: 'bg-red-100 text-red-700', icon: Shield },
    admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700', icon: Crown },
    operator: { label: 'Operatore', color: 'bg-gray-100 text-gray-700', icon: User }
  };
  const config = roleConfig[role] || roleConfig.operator;
  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      <config.icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

// ============ TENANT DETAIL MODAL ============
import { Trash2, Edit, Settings, Thermometer, ToggleLeft, ToggleRight, Save, Wifi } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';

const TenantDetailModal = ({ tenant, onClose, onUpdate, saasToken }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [testingTuya, setTestingTuya] = useState(false);
  const [tuyaDevices, setTuyaDevices] = useState([]);
  
  // Stato per backup e log
  const [tenantBackups, setTenantBackups] = useState([]);
  const [tenantLogs, setTenantLogs] = useState([]);
  const [tenantStats, setTenantStats] = useState(null);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  
  // Stato per gestione database tenant
  const [dbStatus, setDbStatus] = useState(null);
  const [loadingDbStatus, setLoadingDbStatus] = useState(false);
  const [cleaningTenant, setCleaningTenant] = useState(false);
  const [removingDuplicates, setRemovingDuplicates] = useState(false);
  const [duplicatesInfo, setDuplicatesInfo] = useState(null);
  const [confirmClean, setConfirmClean] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    nome: tenant.nome || '',
    ragione_sociale: tenant.ragione_sociale || '',
    partita_iva: tenant.partita_iva || '',
    codice_fiscale: tenant.codice_fiscale || '',
    indirizzo: tenant.indirizzo || '',
    cap: tenant.cap || '',
    citta: tenant.citta || '',
    provincia: tenant.provincia || '',
    telefono: tenant.telefono || '',
    email: tenant.email || '',
    pec: tenant.pec || '',
    codice_sdi: tenant.codice_sdi || '',
    status: tenant.status || 'active',
    plan: tenant.plan || 'free',
    max_users: tenant.max_users || 0,
    data_scadenza: tenant.data_scadenza ? tenant.data_scadenza.split('T')[0] : ''
  });

  // Tuya Config - solo credenziali, i dispositivi vengono rilevati automaticamente
  const [tuyaConfig, setTuyaConfig] = useState({
    access_id: '',
    access_secret: '',
    api_region: 'eu',
    enabled: false
  });

  // Sections
  const [sections, setSections] = useState({
    ordini: true,
    ddt: true,
    haccp: true,
    fb_cost: true,
    menu: true,
    spese: true,
    magazzino: true,
    fatture: false,
    team: true
  });

  // Load Tuya config and sections on mount
  useEffect(() => {
    loadTuyaConfig();
    loadSections();
  }, []);

  const loadTuyaConfig = async () => {
    try {
      const response = await axios.get(`${API}/saas/tenants/${tenant.id}/tuya`, {
        headers: { 'Authorization': `Bearer ${saasToken}` }
      });
      if (response.data.config) {
        setTuyaConfig(response.data.config);
      }
    } catch (error) {
      console.error('Error loading Tuya config:', error);
    }
  };

  const loadSections = async () => {
    try {
      const response = await axios.get(`${API}/saas/tenants/${tenant.id}/sections`, {
        headers: { 'Authorization': `Bearer ${saasToken}` }
      });
      if (response.data.sections) {
        setSections(response.data.sections);
      }
    } catch (error) {
      console.error('Error loading sections:', error);
    }
  };

  // Funzioni per backup e log tenant
  const loadTenantBackups = async () => {
    setLoadingBackups(true);
    try {
      const tenantDbName = tenant.database_name || tenant.id;
      const response = await axios.get(`${API}/controlpanel/tenant-management/${tenantDbName}/backups`, {
        headers: { 'Authorization': `Bearer ${saasToken}` }
      });
      setTenantBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error loading backups:', error);
      setTenantBackups([]);
    } finally {
      setLoadingBackups(false);
    }
  };

  const loadTenantLogs = async () => {
    setLoadingLogs(true);
    try {
      const tenantDbName = tenant.database_name || tenant.id;
      const response = await axios.get(`${API}/controlpanel/tenant-management/${tenantDbName}/logs?limit=50`, {
        headers: { 'Authorization': `Bearer ${saasToken}` }
      });
      setTenantLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error loading logs:', error);
      setTenantLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const loadTenantStats = async () => {
    try {
      const tenantDbName = tenant.database_name || tenant.id;
      const response = await axios.get(`${API}/controlpanel/tenant-management/${tenantDbName}/stats`, {
        headers: { 'Authorization': `Bearer ${saasToken}` }
      });
      setTenantStats(response.data.stats || null);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // === FUNZIONI GESTIONE DATABASE TENANT ===
  
  const loadDbStatus = async () => {
    setLoadingDbStatus(true);
    try {
      // Estrai il tenant ID senza prefisso per l'header
      const tenantDbName = tenant.database_name || tenant.id;
      const tenantHeader = tenantDbName.replace(/^tenant_/, '');
      
      const response = await axios.get(`${API}/gestione/ricette/utilita/stato-tenant`, {
        headers: { 
          'Authorization': `Bearer ${saasToken}`,
          'X-Tenant-DB': tenantHeader
        }
      });
      setDbStatus(response.data);
    } catch (error) {
      console.error('Error loading DB status:', error);
      toast.error('Errore nel caricamento stato database');
    } finally {
      setLoadingDbStatus(false);
    }
  };

  const checkDuplicates = async () => {
    try {
      const tenantDbName = tenant.database_name || tenant.id;
      const tenantHeader = tenantDbName.replace(/^tenant_/, '');
      
      const response = await axios.get(`${API}/gestione/ricette/utilita/verifica-duplicati`, {
        headers: { 
          'Authorization': `Bearer ${saasToken}`,
          'X-Tenant-DB': tenantHeader
        }
      });
      setDuplicatesInfo(response.data);
      if (response.data.duplicati_trovati > 0) {
        toast.warning(`Trovati ${response.data.duplicati_trovati} duplicati!`);
      } else {
        toast.success('Nessun duplicato trovato');
      }
    } catch (error) {
      console.error('Error checking duplicates:', error);
      toast.error('Errore nella verifica duplicati');
    }
  };

  const removeDuplicates = async () => {
    if (!window.confirm('Rimuovere tutti i duplicati trovati? Questa operazione non può essere annullata.')) return;
    
    setRemovingDuplicates(true);
    try {
      const tenantDbName = tenant.database_name || tenant.id;
      const tenantHeader = tenantDbName.replace(/^tenant_/, '');
      
      const response = await axios.post(`${API}/gestione/ricette/utilita/rimuovi-duplicati`, {}, {
        headers: { 
          'Authorization': `Bearer ${saasToken}`,
          'X-Tenant-DB': tenantHeader
        }
      });
      toast.success(response.data.message || `Rimossi ${response.data.duplicati_rimossi || 0} duplicati`);
      setDuplicatesInfo(null);
      loadDbStatus(); // Ricarica stato
    } catch (error) {
      console.error('Error removing duplicates:', error);
      toast.error('Errore nella rimozione duplicati');
    } finally {
      setRemovingDuplicates(false);
    }
  };

  const cleanTenantData = async () => {
    if (!confirmClean) {
      setConfirmClean(true);
      return;
    }
    
    setCleaningTenant(true);
    try {
      const tenantDbName = tenant.database_name || tenant.id;
      const tenantHeader = tenantDbName.replace(/^tenant_/, '');
      
      const response = await axios.post(`${API}/gestione/ricette/utilita/svuota-tenant?conferma=CONFERMO`, {}, {
        headers: { 
          'Authorization': `Bearer ${saasToken}`,
          'X-Tenant-DB': tenantHeader
        }
      });
      toast.success(response.data.message || 'Tenant svuotato con successo');
      setConfirmClean(false);
      loadDbStatus(); // Ricarica stato
    } catch (error) {
      console.error('Error cleaning tenant:', error);
      toast.error('Errore nella pulizia tenant');
    } finally {
      setCleaningTenant(false);
    }
  };

  const createTenantBackup = async () => {
    setCreatingBackup(true);
    try {
      const tenantDbName = tenant.database_name || tenant.id;
      const response = await axios.post(`${API}/controlpanel/tenant-management/${tenantDbName}/backup`, 
        { type: 'manual', created_by: 'saas_admin' },
        { headers: { 'Authorization': `Bearer ${saasToken}` } }
      );
      toast.success(response.data.message || 'Backup creato con successo');
      loadTenantBackups();
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Errore nella creazione del backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const restoreTenantBackup = async (backupId) => {
    if (!window.confirm('⚠️ ATTENZIONE: Questa operazione sovrascriverà tutti i dati attuali del tenant. Continuare?')) return;
    
    try {
      const tenantDbName = tenant.database_name || tenant.id;
      const response = await axios.post(`${API}/controlpanel/tenant-management/${tenantDbName}/restore/${backupId}`, {},
        { headers: { 'Authorization': `Bearer ${saasToken}` } }
      );
      toast.success(response.data.message || 'Ripristino completato');
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast.error('Errore nel ripristino del backup');
    }
  };

  const downloadTenantData = async () => {
    try {
      const tenantDbName = tenant.database_name || tenant.id;
      toast.info('Download in corso...');
      const response = await axios.get(`${API}/controlpanel/tenant-management/${tenantDbName}/download`, {
        headers: { 'Authorization': `Bearer ${saasToken}` }
      });
      
      const dataStr = JSON.stringify(response.data.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tenantDbName}_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Download completato!');
    } catch (error) {
      console.error('Error downloading data:', error);
      toast.error('Errore nel download dei dati');
    }
  };

  // Carica dati quando si cambia tab
  useEffect(() => {
    if (activeTab === 'backup') {
      loadTenantBackups();
      loadTenantStats();
    } else if (activeTab === 'logs') {
      loadTenantLogs();
    }
  }, [activeTab]);

  const handleSaveInfo = async () => {
    setSaving(true);
    try {
      // Salva dati tenant generali
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== 'max_users') {
          params.append(key, value);
        }
      });
      
      await axios.put(`${API}/saas/tenants/${tenant.id}/full?${params.toString()}`, {}, {
        headers: { 'Authorization': `Bearer ${saasToken}` }
      });
      
      // Salva limite utenti separatamente
      await axios.put(`${API}/saas/tenants/${tenant.id}/max-users`, 
        { max_users: formData.max_users },
        { headers: { 'Authorization': `Bearer ${saasToken}` } }
      );
      
      toast.success('Dati tenant salvati');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTuya = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/saas/tenants/${tenant.id}/tuya`, tuyaConfig, {
        headers: { 'Authorization': `Bearer ${saasToken}` }
      });
      toast.success('Configurazione Tuya salvata');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio Tuya');
    } finally {
      setSaving(false);
    }
  };

  const handleTestTuya = async () => {
    if (!tuyaConfig.access_id || !tuyaConfig.access_secret) {
      toast.error('Inserisci Access ID e Access Secret prima di testare');
      return;
    }
    
    // Prima salva la configurazione
    setSaving(true);
    try {
      await axios.put(`${API}/saas/tenants/${tenant.id}/tuya`, tuyaConfig, {
        headers: { 'Authorization': `Bearer ${saasToken}` }
      });
    } catch (error) {
      toast.error('Errore nel salvataggio della configurazione');
      setSaving(false);
      return;
    }
    setSaving(false);
    
    // Poi testa la connessione
    setTestingTuya(true);
    setTuyaDevices([]);
    try {
      const response = await axios.post(`${API}/saas/tenants/${tenant.id}/tuya/test`, {}, {
        headers: { 'Authorization': `Bearer ${saasToken}` }
      });
      setTuyaDevices(response.data.dispositivi || []);
      toast.success(response.data.message || 'Connessione riuscita!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella connessione Tuya');
    } finally {
      setTestingTuya(false);
    }
  };

  const handleSaveSections = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/saas/tenants/${tenant.id}/sections`, sections, {
        headers: { 'Authorization': `Bearer ${saasToken}` }
      });
      toast.success('Sezioni aggiornate');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio sezioni');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/saas/tenants/${tenant.id}`, {
        headers: { 'Authorization': `Bearer ${saasToken}` }
      });
      toast.success('Tenant eliminato');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  const removeDevice = (deviceId) => {
    setTuyaConfig({
      ...tuyaConfig,
      devices: tuyaConfig.devices.filter(d => d.id !== deviceId && d.device_id !== deviceId)
    });
  };

  const sectionLabels = {
    ordini: 'Ordini',
    ddt: 'DDT/Bolle',
    haccp: 'HACCP',
    fb_cost: 'F&B Cost',
    menu: 'Menu',
    spese: 'Costi/Ricavi',
    magazzino: 'Magazzino',
    fatture: 'Fatture Elettroniche',
    team: 'Team'
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between border-b">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {tenant.nome}
            </CardTitle>
            <CardDescription>{tenant.ragione_sociale || tenant.database_name}</CardDescription>
          </div>
          <div className="flex gap-2">
            <StatusBadge status={tenant.status} />
            <PlanBadge plan={tenant.plan} />
          </div>
        </CardHeader>

        {/* Tabs */}
        <div className="border-b px-6">
          <div className="flex gap-4 overflow-x-auto">
            {[
              { id: 'info', label: 'Informazioni', icon: Edit },
              { id: 'database', label: 'Gestione DB', icon: Database },
              { id: 'backup', label: 'Backup & Dati', icon: Archive },
              { id: 'logs', label: 'Log Attività', icon: Activity },
              { id: 'tuya', label: 'Sensori Tuya', icon: Thermometer },
              { id: 'sections', label: 'Sezioni', icon: ToggleLeft },
              { id: 'danger', label: 'Zona Pericolosa', icon: AlertTriangle }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  // Carica stato DB quando si apre la tab
                  if (tab.id === 'database' && !dbStatus) {
                    loadDbStatus();
                  }
                }}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <CardContent className="flex-1 overflow-y-auto p-6">
          {/* Info Tab */}
          {activeTab === 'info' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome Ristorante</Label>
                  <Input value={formData.nome} onChange={e => setFormData({...formData, nome: e.target.value})} />
                </div>
                <div>
                  <Label>Ragione Sociale</Label>
                  <Input value={formData.ragione_sociale} onChange={e => setFormData({...formData, ragione_sociale: e.target.value})} />
                </div>
                <div>
                  <Label>Partita IVA</Label>
                  <Input value={formData.partita_iva} onChange={e => setFormData({...formData, partita_iva: e.target.value})} />
                </div>
                <div>
                  <Label>Codice Fiscale</Label>
                  <Input value={formData.codice_fiscale} onChange={e => setFormData({...formData, codice_fiscale: e.target.value})} />
                </div>
                <div className="col-span-2">
                  <Label>Indirizzo</Label>
                  <Input value={formData.indirizzo} onChange={e => setFormData({...formData, indirizzo: e.target.value})} />
                </div>
                <div>
                  <Label>CAP</Label>
                  <Input value={formData.cap} onChange={e => setFormData({...formData, cap: e.target.value})} />
                </div>
                <div>
                  <Label>Città</Label>
                  <Input value={formData.citta} onChange={e => setFormData({...formData, citta: e.target.value})} />
                </div>
                <div>
                  <Label>Provincia</Label>
                  <Input value={formData.provincia} onChange={e => setFormData({...formData, provincia: e.target.value})} maxLength={2} />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input value={formData.telefono} onChange={e => setFormData({...formData, telefono: e.target.value})} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                </div>
                <div>
                  <Label>PEC</Label>
                  <Input type="email" value={formData.pec} onChange={e => setFormData({...formData, pec: e.target.value})} />
                </div>
                <div>
                  <Label>Codice SDI</Label>
                  <Input value={formData.codice_sdi} onChange={e => setFormData({...formData, codice_sdi: e.target.value})} maxLength={7} />
                </div>
                <div>
                  <Label>Stato</Label>
                  <Select value={formData.status} onValueChange={v => setFormData({...formData, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Attivo</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="suspended">Sospeso</SelectItem>
                      <SelectItem value="cancelled">Cancellato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Piano</Label>
                  <Select value={formData.plan} onValueChange={v => setFormData({...formData, plan: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="starter">Starter</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-red-500" />
                    Data Scadenza
                  </Label>
                  <Input 
                    type="date" 
                    value={formData.data_scadenza} 
                    onChange={e => setFormData({...formData, data_scadenza: e.target.value})}
                    className={formData.data_scadenza && new Date(formData.data_scadenza) < new Date() ? 'border-red-500 bg-red-50' : ''}
                  />
                  {formData.data_scadenza && new Date(formData.data_scadenza) < new Date() && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Abbonamento scaduto! Il tenant non può accedere.
                    </p>
                  )}
                  {!formData.data_scadenza && (
                    <p className="text-xs text-gray-500 mt-1">Lascia vuoto per accesso illimitato</p>
                  )}
                </div>
              </div>
              
              {/* Limite utenti */}
              <div className="p-4 bg-violet-50 rounded-lg space-y-3">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-violet-500" />
                  <div>
                    <p className="font-medium">Limite Utenti</p>
                    <p className="text-sm text-gray-500">
                      Piano {formData.plan?.charAt(0).toUpperCase() + formData.plan?.slice(1)}: 
                      {formData.plan === 'free' && ' 1 utente'}
                      {formData.plan === 'starter' && ' 3 utenti'}
                      {formData.plan === 'professional' && ' 10 utenti'}
                      {formData.plan === 'enterprise' && ' Illimitati'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between pl-8">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Override manuale (opzionale)</p>
                    <p className="text-xs text-gray-500">0 = usa limite del piano</p>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    value={formData.max_users}
                    onChange={(e) => setFormData({...formData, max_users: parseInt(e.target.value) || 0})}
                    className="w-24 text-center"
                  />
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button onClick={handleSaveInfo} disabled={saving}>
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Salva Modifiche
                </Button>
              </div>
            </div>
          )}

          {/* Database Management Tab */}
          {activeTab === 'database' && (
            <div className="space-y-6">
              {/* Header con azioni */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Database className="h-5 w-5 text-blue-500" />
                    Gestione Database Tenant
                  </h3>
                  <p className="text-sm text-gray-500">
                    Database: <code className="bg-gray-100 px-2 py-1 rounded">{tenant.database_name}</code>
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={loadDbStatus}
                  disabled={loadingDbStatus}
                >
                  {loadingDbStatus ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  <span className="ml-2">Aggiorna</span>
                </Button>
              </div>

              {/* Stato Collezioni */}
              {dbStatus && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Archive className="h-4 w-4" />
                      Stato Collezioni
                    </CardTitle>
                    <CardDescription>
                      Totale documenti: <span className="font-bold text-blue-600">{dbStatus.totale_documenti || 0}</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {Object.entries(dbStatus.collezioni || {}).map(([name, count]) => (
                        <div 
                          key={name} 
                          className={`p-3 rounded-lg border ${
                            count > 0 ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <p className="text-sm font-medium text-gray-700">{name}</p>
                          <p className={`text-xl font-bold ${count > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                            {count}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Verifica Duplicati */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    Verifica Duplicati
                  </CardTitle>
                  <CardDescription>
                    Controlla e rimuovi eventuali ricette duplicate
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={checkDuplicates}>
                      <Search className="h-4 w-4 mr-2" />
                      Cerca Duplicati
                    </Button>
                    {duplicatesInfo && duplicatesInfo.duplicati_trovati > 0 && (
                      <Button 
                        variant="destructive" 
                        onClick={removeDuplicates}
                        disabled={removingDuplicates}
                      >
                        {removingDuplicates ? (
                          <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Trash2 className="h-4 w-4 mr-2" />
                        )}
                        Rimuovi {duplicatesInfo.duplicati_trovati} Duplicati
                      </Button>
                    )}
                  </div>
                  
                  {duplicatesInfo && (
                    <div className={`p-3 rounded-lg ${
                      duplicatesInfo.duplicati_trovati > 0 
                        ? 'bg-yellow-50 border border-yellow-200' 
                        : 'bg-green-50 border border-green-200'
                    }`}>
                      {duplicatesInfo.duplicati_trovati > 0 ? (
                        <div>
                          <p className="font-medium text-yellow-700">
                            ⚠️ Trovati {duplicatesInfo.duplicati_trovati} duplicati
                          </p>
                          <ul className="mt-2 text-sm text-yellow-600">
                            {duplicatesInfo.dettaglio?.slice(0, 5).map((d, i) => (
                              <li key={i}>• {d.nome}: {d.count} copie</li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <p className="text-green-700">✓ Nessun duplicato trovato</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pulizia Tenant - ZONA PERICOLOSA */}
              <Card className="border-red-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-red-600">
                    <AlertTriangle className="h-4 w-4" />
                    Pulizia Completa Tenant
                  </CardTitle>
                  <CardDescription>
                    Elimina TUTTI i dati operativi (ricette, DDT, ordini, fatture...). 
                    Mantiene solo le configurazioni (Tuya, impostazioni).
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-red-700 mb-4">
                      <strong>⚠️ ATTENZIONE:</strong> Questa operazione è irreversibile! 
                      Tutti i dati del tenant verranno eliminati permanentemente.
                    </p>
                    
                    {!confirmClean ? (
                      <Button 
                        variant="outline" 
                        className="border-red-300 text-red-600 hover:bg-red-100"
                        onClick={() => setConfirmClean(true)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Svuota Tenant
                      </Button>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-red-700">
                          Sei sicuro? Clicca di nuovo per confermare.
                        </p>
                        <div className="flex gap-3">
                          <Button 
                            variant="destructive" 
                            onClick={cleanTenantData}
                            disabled={cleaningTenant}
                          >
                            {cleaningTenant ? (
                              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Trash2 className="h-4 w-4 mr-2" />
                            )}
                            CONFERMO - Elimina Tutto
                          </Button>
                          <Button 
                            variant="outline" 
                            onClick={() => setConfirmClean(false)}
                          >
                            Annulla
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Backup Tab */}
          {activeTab === 'backup' && (
            <div className="space-y-6">
              {/* Statistiche Tenant */}
              {tenantStats && (
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-2xl font-bold">{tenantStats.total_documents || 0}</p>
                      <p className="text-xs text-gray-500">Documenti totali</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-2xl font-bold">{Object.keys(tenantStats.collections || {}).length}</p>
                      <p className="text-xs text-gray-500">Collections</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm font-medium">{tenantStats.last_activity ? new Date(tenantStats.last_activity).toLocaleString('it-IT') : '-'}</p>
                      <p className="text-xs text-gray-500">Ultima attività</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Azioni principali */}
              <div className="flex gap-3">
                <Button onClick={downloadTenantData} variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Scarica Tutti i Dati
                </Button>
                <Button onClick={createTenantBackup} disabled={creatingBackup} className="flex items-center gap-2">
                  {creatingBackup ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                  Crea Backup
                </Button>
              </div>

              {/* Lista Backup */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Archive className="h-4 w-4" />
                  Backup Disponibili
                </h4>
                {loadingBackups ? (
                  <div className="text-center py-4">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                  </div>
                ) : tenantBackups.length > 0 ? (
                  <div className="space-y-2">
                    {tenantBackups.map((backup, idx) => (
                      <div key={backup._id || idx} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <Badge className={
                              backup.type === 'daily' ? 'bg-blue-100 text-blue-700' :
                              backup.type === 'manual' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-700'
                            }>
                              {backup.type || 'backup'}
                            </Badge>
                            <span className="text-sm text-gray-500 ml-2">
                              {backup.created_at ? new Date(backup.created_at).toLocaleString('it-IT') : '-'}
                            </span>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => restoreTenantBackup(backup._id)}
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          >
                            <History className="h-4 w-4 mr-1" />
                            Ripristina
                          </Button>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {backup.total_documents || 0} documenti • {backup.collections?.length || 0} collections
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Nessun backup disponibile</p>
                )}
              </div>
            </div>
          )}

          {/* Logs Tab */}
          {activeTab === 'logs' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Log Attività Utenti
                </h4>
                <Button variant="outline" size="sm" onClick={loadTenantLogs}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${loadingLogs ? 'animate-spin' : ''}`} />
                  Aggiorna
                </Button>
              </div>
              
              {loadingLogs ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                </div>
              ) : tenantLogs.length > 0 ? (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {tenantLogs.map((log, idx) => (
                    <div key={log._id || idx} className="bg-gray-50 rounded-lg p-3 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <Badge className={
                          log.action_type?.includes('create') ? 'bg-green-100 text-green-700' :
                          log.action_type?.includes('delete') ? 'bg-red-100 text-red-700' :
                          log.action_type?.includes('update') ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }>
                          {log.action_type || 'action'}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString('it-IT') : '-'}
                        </span>
                      </div>
                      <p className="text-gray-600">{log.user_id || 'system'}</p>
                      {log.details && (
                        <pre className="text-xs bg-white mt-1 p-2 rounded overflow-x-auto">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Nessun log disponibile</p>
              )}
            </div>
          )}

          {/* Tuya Tab */}
          {activeTab === 'tuya' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Wifi className="h-5 w-5 text-blue-500" />
                  <div>
                    <p className="font-medium">Integrazione Tuya/Smart Life</p>
                    <p className="text-sm text-gray-500">Abilita la lettura automatica dei sensori temperatura</p>
                  </div>
                </div>
                <Switch 
                  checked={tuyaConfig.enabled} 
                  onCheckedChange={async (v) => {
                    const newConfig = {...tuyaConfig, enabled: v};
                    setTuyaConfig(newConfig);
                    // Salva automaticamente quando si disabilita
                    if (!v) {
                      try {
                        await axios.put(`${API}/saas/tenants/${tenant.id}/tuya`, newConfig, {
                          headers: { 'Authorization': `Bearer ${saasToken}` }
                        });
                        toast.success('Sensori Tuya disabilitati');
                      } catch (error) {
                        toast.error('Errore nel salvataggio');
                      }
                    }
                  }}
                />
              </div>

              {tuyaConfig.enabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Access ID (Client ID)</Label>
                      <Input 
                        value={tuyaConfig.access_id} 
                        onChange={e => setTuyaConfig({...tuyaConfig, access_id: e.target.value})}
                        placeholder="Tuya Access ID"
                        data-testid="tuya-access-id"
                      />
                    </div>
                    <div>
                      <Label>Access Secret</Label>
                      <Input 
                        type="password"
                        value={tuyaConfig.access_secret} 
                        onChange={e => setTuyaConfig({...tuyaConfig, access_secret: e.target.value})}
                        placeholder="Tuya Access Secret"
                        data-testid="tuya-access-secret"
                      />
                    </div>
                    <div>
                      <Label>Regione API</Label>
                      <Select value={tuyaConfig.api_region} onValueChange={v => setTuyaConfig({...tuyaConfig, api_region: v})}>
                        <SelectTrigger data-testid="tuya-region"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="eu">Europa (eu)</SelectItem>
                          <SelectItem value="us">USA (us)</SelectItem>
                          <SelectItem value="cn">Cina (cn)</SelectItem>
                          <SelectItem value="in">India (in)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Test Connection Button */}
                  <div className="flex gap-3">
                    <Button 
                      onClick={handleTestTuya} 
                      disabled={testingTuya || !tuyaConfig.access_id || !tuyaConfig.access_secret}
                      variant="outline"
                      className="flex-1"
                      data-testid="tuya-test-btn"
                    >
                      {testingTuya ? (
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Wifi className="h-4 w-4 mr-2" />
                      )}
                      Test Connessione e Scopri Dispositivi
                    </Button>
                    <Button onClick={handleSaveTuya} disabled={saving} data-testid="tuya-save-btn">
                      {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Salva
                    </Button>
                  </div>

                  {/* Dispositivi Rilevati */}
                  {tuyaDevices.length > 0 && (
                    <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                      <div className="flex items-center gap-2 mb-3">
                        <Thermometer className="h-5 w-5 text-green-600" />
                        <Label className="text-green-700 font-semibold">
                          Dispositivi Rilevati ({tuyaDevices.length})
                        </Label>
                      </div>
                      <div className="space-y-2">
                        {tuyaDevices.map((device, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div className="flex items-center gap-3">
                              <div className={`w-3 h-3 rounded-full ${device.online ? 'bg-green-500' : 'bg-gray-300'}`} />
                              <div>
                                <p className="font-medium text-gray-900">{device.nome}</p>
                                <p className="text-xs text-gray-500 font-mono">{device.id}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              {device.temperatura !== null && device.temperatura !== undefined ? (
                                <p className="text-lg font-bold text-blue-600">{device.temperatura}°C</p>
                              ) : (
                                <p className="text-sm text-gray-400">-</p>
                              )}
                              {device.umidita && (
                                <p className="text-xs text-gray-500">{device.umidita}% umidità</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-green-600 mt-3">
                        ✓ Questi dispositivi saranno disponibili per il monitoraggio HACCP del tenant
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Sections Tab */}
          {activeTab === 'sections' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-500">
                Abilita o disabilita le sezioni disponibili per questo tenant.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(sectionLabels).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="font-medium">{label}</span>
                    <Switch 
                      checked={sections[key]} 
                      onCheckedChange={v => setSections({...sections, [key]: v})}
                    />
                  </div>
                ))}
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveSections} disabled={saving}>
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Salva Sezioni
                </Button>
              </div>
            </div>
          )}

          {/* Danger Zone Tab */}
          {activeTab === 'danger' && (
            <div className="space-y-6">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-red-700">Elimina Tenant</h3>
                    <p className="text-sm text-red-600 mt-1">
                      Questa azione è <strong>irreversibile</strong>. Verranno eliminati:
                    </p>
                    <ul className="text-sm text-red-600 mt-2 list-disc list-inside">
                      <li>Tutti i dati del tenant</li>
                      <li>Il database "{tenant.database_name}"</li>
                      <li>Tutti gli utenti associati</li>
                    </ul>
                  </div>
                </div>
              </div>

              {!confirmDelete ? (
                <Button 
                  variant="destructive" 
                  onClick={() => setConfirmDelete(true)}
                  className="w-full"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Elimina Tenant
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-center text-sm font-medium text-red-600">
                    Sei sicuro? Scrivi "{tenant.nome}" per confermare
                  </p>
                  <Input 
                    placeholder={`Scrivi "${tenant.nome}" per confermare`}
                    id="confirm-delete"
                  />
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setConfirmDelete(false)} className="flex-1">
                      Annulla
                    </Button>
                    <Button 
                      variant="destructive" 
                      className="flex-1"
                      disabled={deleting}
                      onClick={() => {
                        const input = document.getElementById('confirm-delete');
                        if (input.value === tenant.nome) {
                          handleDelete();
                        } else {
                          toast.error('Il nome non corrisponde');
                        }
                      }}
                    >
                      {deleting ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                      Conferma Eliminazione
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>

        <div className="border-t p-4 flex justify-end">
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
        </div>
      </Card>
    </div>
  );
};

const SaaSAdminPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [searchTenant, setSearchTenant] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  
  // Modals
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showTenantDetail, setShowTenantDetail] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(null);
  const [showAddTenantAccess, setShowAddTenantAccess] = useState(null);
  const [showEditPlans, setShowEditPlans] = useState(false);
  const [editingPlans, setEditingPlans] = useState([]);
  const [showResetPassword, setShowResetPassword] = useState(null); // { user, type: 'user' | 'tenant_user' | 'saas_user' }
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Notifiche Push Test
  const [notificationStatus, setNotificationStatus] = useState(null);
  const [notificationStatusLoading, setNotificationStatusLoading] = useState(true);
  const [testNotifTitle, setTestNotifTitle] = useState('🔔 Test Notifica Ristorantissimo');
  const [testNotifMessage, setTestNotifMessage] = useState('Se ricevi questa notifica, il sistema push funziona correttamente!');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [selectedNotifTenant, setSelectedNotifTenant] = useState('');
  
  // Form data
  const [newTenant, setNewTenant] = useState({
    nome: '', ragione_sociale: '', partita_iva: '', email: '',
    telefono: '', indirizzo: '', cap: '', citta: '', provincia: ''
  });
  const [newUser, setNewUser] = useState({
    username: '', email: '', password: '', nome: '', cognome: '',
    tenant_id: '', role: 'operator'
  });
  
  const [saving, setSaving] = useState(false);
  const [searchingPiva, setSearchingPiva] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showSetupForm, setShowSetupForm] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [setupData, setSetupData] = useState({ username: '', password: '', confirmPassword: '' });
  const [loginError, setLoginError] = useState('');
  const [setupError, setSetupError] = useState('');
  
  // Cassetto Fiscale States
  const [cassettoClients, setCassettoClients] = useState([]);
  const [cassettoLoading, setCassettoLoading] = useState(false);
  const [cassettoStatus, setCassettoStatus] = useState(null);
  const [showAddCassettoClient, setShowAddCassettoClient] = useState(false);
  const [newCassettoClient, setNewCassettoClient] = useState({
    nome: '', codice_fiscale: '', tipo: 'persona_legale', email: '', tenant_db_target: ''
  });
  const [cassettoSearchTerm, setCassettoSearchTerm] = useState('');
  const [cassettoActionLoading, setCassettoActionLoading] = useState(null);
  
  // FiscoAPI Settings States
  const [showFiscoApiSettings, setShowFiscoApiSettings] = useState(false);
  const [fiscoApiKey, setFiscoApiKey] = useState('');
  const [fiscoApiUsername, setFiscoApiUsername] = useState('');
  const [fiscoApiPassword, setFiscoApiPassword] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [savingFiscoApiSettings, setSavingFiscoApiSettings] = useState(false);
  
  // Reset Production States
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resettingProduction, setResettingProduction] = useState(false);

  // Handle Full Reset
  const handleFullReset = async () => {
    if (!resetPassword) {
      toast.error('Inserisci la password di conferma');
      return;
    }
    
    setResettingProduction(true);
    try {
      const response = await axios.post(`${API}/saas/auth/full-reset`, {
        confirm_password: resetPassword
      });
      
      if (response.data.success) {
        toast.success('Reset completato! Ricarica la pagina...');
        setShowResetModal(false);
        // Pulisci localStorage e ricarica
        localStorage.clear();
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il reset');
    } finally {
      setResettingProduction(false);
    }
  };

  // Cerca dati societari tramite P.IVA
  const handleSearchPiva = async () => {
    if (!newTenant.partita_iva || newTenant.partita_iva.length < 11) {
      toast.error('Inserisci una Partita IVA valida (11 cifre)');
      return;
    }
    
    setSearchingPiva(true);
    try {
      const response = await axios.get(`${API}/saas/lookup/partita-iva/${newTenant.partita_iva}`);
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setNewTenant(prev => ({
          ...prev,
          ragione_sociale: data.ragione_sociale || prev.ragione_sociale,
          indirizzo: data.indirizzo || prev.indirizzo,
          cap: data.cap || prev.cap,
          citta: data.citta || prev.citta,
          provincia: data.provincia || prev.provincia
        }));
        toast.success('Dati societari trovati e compilati!');
      } else {
        toast.error(response.data.error || 'P.IVA non trovata');
      }
    } catch (error) {
      console.error('Errore ricerca P.IVA:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la ricerca');
    } finally {
      setSearchingPiva(false);
    }
  };

  // Get SaaS token from localStorage or use main token
  const getToken = () => {
    return localStorage.getItem('saas_token') || localStorage.getItem('token');
  };

  // SaaS Login
  const handleSaaSLogin = async () => {
    if (!loginData.username || !loginData.password) {
      setLoginError('Inserisci username e password');
      return;
    }
    
    setSaving(true);
    setLoginError('');
    try {
      const response = await axios.post(`${API}/saas/auth/login`, loginData);
      localStorage.setItem('saas_token', response.data.access_token);
      setIsAuthenticated(true);
      setShowLoginForm(false);
      toast.success('Login Super Admin effettuato!');
      // Ricarica dati
      loadStats();
      loadTenants();
      loadUsers();
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Credenziali non valide');
    } finally {
      setSaving(false);
    }
  };

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      // Prima verifica se serve il setup iniziale
      try {
        const setupCheck = await axios.get(`${API}/saas/auth/check-setup`);
        if (setupCheck.data.needs_setup) {
          setShowSetupForm(true);
          setShowLoginForm(false);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('Error checking setup:', error);
      }
      
      const token = getToken();
      if (!token) {
        setShowLoginForm(true);
        setLoading(false);
        return;
      }
      
      try {
        // Decodifica il token per verificare is_super_admin
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.is_super_admin) {
          setIsAuthenticated(true);
          setLoading(false);
          return;
        }
        
        // Altrimenti prova con l'endpoint
        const response = await axios.get(`${API}/saas/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data.user?.is_super_admin) {
          setIsAuthenticated(true);
        } else {
          setShowLoginForm(true);
        }
      } catch {
        setShowLoginForm(true);
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  // Handle initial setup
  const handleSetup = async () => {
    if (!setupData.username || !setupData.password) {
      setSetupError('Inserisci username e password');
      return;
    }
    if (setupData.username.length < 3) {
      setSetupError('Username deve essere almeno 3 caratteri');
      return;
    }
    if (setupData.password.length < 8) {
      setSetupError('Password deve essere almeno 8 caratteri');
      return;
    }
    if (setupData.password !== setupData.confirmPassword) {
      setSetupError('Le password non coincidono');
      return;
    }
    
    setSaving(true);
    setSetupError('');
    try {
      await axios.post(`${API}/saas/auth/setup`, {
        username: setupData.username,
        password: setupData.password
      });
      toast.success('Super Admin creato con successo!');
      // Ora fai login automatico
      setLoginData({ username: setupData.username, password: setupData.password });
      setShowSetupForm(false);
      setShowLoginForm(true);
      // Login automatico
      const response = await axios.post(`${API}/saas/auth/login`, {
        username: setupData.username,
        password: setupData.password
      });
      localStorage.setItem('saas_token', response.data.access_token);
      setIsAuthenticated(true);
      setShowLoginForm(false);
    } catch (error) {
      setSetupError(error.response?.data?.detail || 'Errore durante il setup');
    } finally {
      setSaving(false);
    }
  };

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/saas/stats/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      if (error.response?.status === 403) {
        toast.error('Accesso non autorizzato. Solo Super Admin può accedere.');
      }
    }
  }, []);

  // Load tenants
  const loadTenants = useCallback(async () => {
    try {
      const token = getToken();
      let url = `${API}/saas/tenants?limit=100`;
      if (filterStatus !== 'all') url += `&status=${filterStatus}`;
      if (filterPlan !== 'all') url += `&plan=${filterPlan}`;
      if (searchTenant) url += `&search=${encodeURIComponent(searchTenant)}`;
      
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTenants(response.data.tenants);
    } catch (error) {
      console.error('Error loading tenants:', error);
    }
  }, [filterStatus, filterPlan, searchTenant]);

  // Load users
  const loadUsers = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/saas/users?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, []);

  // Load plans
  const loadPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API.replace('/saas', '')}/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error loading plans:', error);
      // Use default plans
      setPlans([
        { id: 'starter', name: 'Starter', price: '29', period: 'mese', features: ['5 utenti', '1.000 prodotti', 'Supporto email', 'HACCP base'], popular: false },
        { id: 'professional', name: 'Professional', price: '79', period: 'mese', features: ['20 utenti', 'Prodotti illimitati', 'Supporto prioritario', 'HACCP + AI OCR', 'Sensori IoT'], popular: true },
        { id: 'enterprise', name: 'Enterprise', price: '199', period: 'mese', features: ['Utenti illimitati', 'Multi-sede', 'Supporto dedicato', 'API personalizzate'], popular: false }
      ]);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadStats(), loadTenants(), loadUsers(), loadPlans()]);
      setLoading(false);
      
      // Carica stato OneSignal
      try {
        const token = localStorage.getItem('saas_token');
        if (token) {
          const res = await axios.get(`${API}/saas/notifications/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setNotificationStatus(res.data);
        }
      } catch (e) {
        console.log('Errore caricamento status OneSignal:', e);
        setNotificationStatus({ configured: false, message: 'Errore caricamento' });
      } finally {
        setNotificationStatusLoading(false);
      }
    };
    loadAll();
  }, [loadStats, loadTenants, loadUsers, loadPlans]);

  // Reload tenants when filters change
  useEffect(() => {
    loadTenants();
  }, [filterStatus, filterPlan, searchTenant, loadTenants]);

  // Create tenant
  const handleCreateTenant = async () => {
    if (!newTenant.nome || !newTenant.email) {
      toast.error('Nome e email sono obbligatori');
      return;
    }
    
    setSaving(true);
    try {
      const token = getToken();
      const response = await axios.post(`${API}/saas/tenants`, newTenant, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data && response.data.tenant) {
        toast.success('Tenant creato con successo!');
        setShowCreateTenant(false);
        setNewTenant({
          nome: '', ragione_sociale: '', partita_iva: '', email: '',
          telefono: '', indirizzo: '', cap: '', citta: '', provincia: ''
        });
        loadTenants();
        loadStats();
      } else {
        toast.error('Risposta non valida dal server');
      }
    } catch (error) {
      console.error('Errore creazione tenant:', error);
      const errorMsg = error.response?.data?.detail 
        || error.response?.data?.error 
        || (typeof error.response?.data === 'string' ? error.response.data : null)
        || error.message 
        || 'Errore nella creazione del tenant';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Reset password
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    setResettingPassword(true);
    try {
      const token = getToken();
      let url = '';
      
      if (showResetPassword.type === 'user') {
        url = `${API}/saas/users/${showResetPassword.user.id}/reset-password`;
      } else if (showResetPassword.type === 'tenant_user') {
        url = `${API}/saas/tenant-users/${showResetPassword.user.id}/reset-password`;
      } else if (showResetPassword.type === 'saas_user') {
        url = `${API}/saas/saas-users/${showResetPassword.user.username}/reset-password`;
      }
      
      await axios.post(url, { new_password: newPassword }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Password resettata con successo!');
      setShowResetPassword(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  // Create user
  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.password) {
      toast.error('Username, email e password sono obbligatori');
      return;
    }
    
    setSaving(true);
    try {
      const token = getToken();
      let url = `${API}/saas/users`;
      if (newUser.tenant_id) {
        url += `?tenant_id=${newUser.tenant_id}&role=${newUser.role}`;
      }
      
      const response = await axios.post(url, {
        username: newUser.username,
        email: newUser.email,
        password: newUser.password,
        nome: newUser.nome,
        cognome: newUser.cognome
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data && response.data.success) {
        toast.success('Utente creato con successo!');
        setShowCreateUser(false);
        setNewUser({
          username: '', email: '', password: '', nome: '', cognome: '',
          tenant_id: '', role: 'operator'
        });
        loadUsers();
        loadStats();
      } else {
        toast.error('Risposta non valida dal server');
      }
    } catch (error) {
      console.error('Errore creazione utente:', error);
      const errorMsg = error.response?.data?.detail 
        || error.response?.data?.error 
        || (typeof error.response?.data === 'string' ? error.response.data : null)
        || error.message 
        || 'Errore nella creazione utente';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Suspend/Activate tenant
  const handleToggleTenantStatus = async (tenant) => {
    try {
      const token = getToken();
      const endpoint = tenant.status === 'suspended' ? 'activate' : 'suspend';
      await axios.post(`${API}/saas/tenants/${tenant.id}/${endpoint}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`Tenant ${endpoint === 'activate' ? 'attivato' : 'sospeso'}!`);
      loadTenants();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Impersonate user
  const handleImpersonate = async (userId) => {
    try {
      const token = getToken();
      const response = await axios.post(`${API}/saas/impersonate/${userId}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Salva token originale per poter tornare indietro
      localStorage.setItem('original_saas_token', token);
      
      // Salva il nuovo token per il tenant
      localStorage.setItem('tenant_token', response.data.token);
      localStorage.setItem('tenant_user', JSON.stringify(response.data.user));
      
      // Se l'utente ha tenant, salva anche quelli
      if (response.data.user.tenants && response.data.user.tenants.length > 0) {
        localStorage.setItem('tenant_list', JSON.stringify(response.data.user.tenants));
      }
      
      toast.success(`Impersonazione attiva! Redirect a /tenant tra 2 secondi...`);
      
      // Redirect automatico dopo 2 secondi
      setTimeout(() => {
        window.location.href = '/tenant';
      }, 2000);
    } catch (error) {
      console.error('Errore impersonazione:', error);
      toast.error(error.response?.data?.detail || 'Errore impersonazione');
    }
  };

  // Add tenant access to user
  const handleAddTenantAccess = async (userId, tenantId, role) => {
    try {
      const token = getToken();
      await axios.post(`${API}/saas/users/${userId}/add-tenant-access?tenant_id=${tenantId}&role=${role}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Accesso al tenant aggiunto!');
      setShowAddTenantAccess(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Remove tenant access from user
  const handleRemoveTenantAccess = async (userId, tenantId) => {
    if (!window.confirm('Rimuovere accesso a questo tenant?')) return;
    
    try {
      const token = getToken();
      await axios.delete(`${API}/saas/users/${userId}/remove-tenant-access/${tenantId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Accesso rimosso!');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // ============ CASSETTO FISCALE FUNCTIONS ============
  const loadCassettoStatus = async () => {
    try {
      const response = await axios.get(`${API}/cassetto-fiscale/status`);
      setCassettoStatus(response.data);
    } catch (error) {
      console.error('Errore status Cassetto Fiscale:', error);
      setCassettoStatus({ status: 'error' });
    }
  };

  const loadFiscoApiSettings = async () => {
    try {
      const response = await axios.get(`${API}/cassetto-fiscale/settings`);
      if (response.data.api_key_masked) {
        setFiscoApiKey(response.data.api_key_masked);
      }
      if (response.data.username_masked) {
        setFiscoApiUsername(response.data.username_masked);
      }
      if (response.data.password_masked) {
        setFiscoApiPassword(response.data.password_masked);
      }
    } catch (error) {
      console.error('Errore caricamento impostazioni FiscoAPI:', error);
    }
  };

  const handleSaveFiscoApiSettings = async () => {
    // Prepara solo i campi che sono stati modificati (non mascherati)
    const updates = {};
    
    if (fiscoApiKey && !fiscoApiKey.includes('*')) {
      updates.api_key = fiscoApiKey;
    }
    if (fiscoApiUsername && !fiscoApiUsername.includes('*')) {
      updates.username = fiscoApiUsername;
    }
    if (fiscoApiPassword && !fiscoApiPassword.includes('*')) {
      updates.password = fiscoApiPassword;
    }
    
    if (Object.keys(updates).length === 0) {
      toast.error('Inserisci almeno una credenziale valida (non mascherata)');
      return;
    }
    
    setSavingFiscoApiSettings(true);
    try {
      await axios.post(`${API}/cassetto-fiscale/settings`, updates);
      toast.success('Credenziali FiscoAPI aggiornate con successo!');
      setShowFiscoApiSettings(false);
      await loadCassettoStatus();
      await loadFiscoApiSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore salvataggio impostazioni');
    } finally {
      setSavingFiscoApiSettings(false);
    }
  };

  const loadCassettoClients = async () => {
    setCassettoLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/cassetto-fiscale/tenants`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Tenant-DB': 'saas_admin'
        }
      });
      setCassettoClients(response.data.tenants || []);
    } catch (error) {
      console.error('Errore caricamento clienti:', error);
    } finally {
      setCassettoLoading(false);
    }
  };

  const handleAddCassettoClient = async () => {
    if (!newCassettoClient.nome || !newCassettoClient.codice_fiscale) {
      toast.error('Inserisci nome e codice fiscale/P.IVA');
      return;
    }
    if (!newCassettoClient.tenant_db_target) {
      toast.error('Seleziona il tenant di destinazione per le fatture');
      return;
    }
    setCassettoActionLoading('add');
    try {
      const token = getToken();
      await axios.post(`${API}/cassetto-fiscale/tenants`, newCassettoClient, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Tenant-DB': 'saas_admin'
        }
      });
      toast.success(`Cliente aggiunto! Fatture verranno salvate in: ${newCassettoClient.tenant_db_target}`);
      setShowAddCassettoClient(false);
      setNewCassettoClient({ nome: '', codice_fiscale: '', tipo: 'persona_legale', email: '', tenant_db_target: '' });
      loadCassettoClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiunta cliente');
    } finally {
      setCassettoActionLoading(null);
    }
  };

  const handleGenerateCassettoLink = async (client) => {
    setCassettoActionLoading(`link-${client.id}`);
    try {
      const token = getToken();
      const response = await axios.post(
        `${API}/cassetto-fiscale/tenants/${client.id}/genera-link`,
        { tenant_id: client.id, anni_fatture: [2024, 2025, 2026] },
        { headers: { 'Authorization': `Bearer ${token}`, 'X-Tenant-DB': 'saas_admin' } }
      );
      if (response.data.link_url) {
        navigator.clipboard.writeText(response.data.link_url);
        toast.success('Link SPID generato e copiato!');
      }
      loadCassettoClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore generazione link');
    } finally {
      setCassettoActionLoading(null);
    }
  };

  const handleCheckCassettoStatus = async (client) => {
    setCassettoActionLoading(`check-${client.id}`);
    try {
      const token = getToken();
      const response = await axios.get(
        `${API}/cassetto-fiscale/tenants/${client.id}/stato-link`,
        { headers: { 'Authorization': `Bearer ${token}`, 'X-Tenant-DB': 'saas_admin' } }
      );
      if (response.data.stato === 'autorizzato') {
        toast.success('Cliente autorizzato!');
      } else {
        toast.info(`Stato: ${response.data.stato}`);
      }
      loadCassettoClients();
    } catch (error) {
      toast.error('Errore verifica stato');
    } finally {
      setCassettoActionLoading(null);
    }
  };

  const handleDownloadCassettoFatture = async (client) => {
    setCassettoActionLoading(`download-${client.id}`);
    try {
      const token = getToken();
      const response = await axios.post(
        `${API}/cassetto-fiscale/tenants/${client.id}/scarica-fatture`,
        {},
        { headers: { 'Authorization': `Bearer ${token}`, 'X-Tenant-DB': 'saas_admin' } }
      );
      toast.success(`Scaricate ${response.data.totale} fatture!`);
      loadCassettoClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore download');
    } finally {
      setCassettoActionLoading(null);
    }
  };

  const handleDeleteCassettoClient = async (client) => {
    if (!window.confirm(`Eliminare ${client.nome}?`)) return;
    try {
      const token = getToken();
      await axios.delete(`${API}/cassetto-fiscale/tenants/${client.id}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'X-Tenant-DB': 'saas_admin' }
      });
      toast.success('Cliente eliminato');
      loadCassettoClients();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const getCassettoStatoBadge = (stato) => {
    const badges = {
      'non_autorizzato': { color: 'bg-gray-100 text-gray-700', text: 'Non autorizzato' },
      'in_attesa': { color: 'bg-amber-100 text-amber-700', text: 'In attesa SPID' },
      'autorizzato': { color: 'bg-green-100 text-green-700', text: 'Autorizzato' },
      'errore': { color: 'bg-red-100 text-red-700', text: 'Errore' }
    };
    const badge = badges[stato] || badges['non_autorizzato'];
    return <Badge className={badge.color}>{badge.text}</Badge>;
  };

  // Load Cassetto data when tab changes
  useEffect(() => {
    if (activeTab === 'cassetto' && isAuthenticated) {
      loadCassettoStatus();
      loadCassettoClients();
      loadFiscoApiSettings();
    }
  }, [activeTab, isAuthenticated]);

  const filteredCassettoClients = cassettoClients.filter(c =>
    c.nome?.toLowerCase().includes(cassettoSearchTerm.toLowerCase()) ||
    c.codice_fiscale?.includes(cassettoSearchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Show setup form if no Super Admin exists
  if (showSetupForm && !isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-4">
              <UserPlus className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Setup Iniziale</CardTitle>
            <CardDescription>
              Crea il primo Super Admin per iniziare
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {setupError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                {setupError}
              </div>
            )}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
              Benvenuto! Questo è il primo accesso. Crea le credenziali per l'amministratore.
            </div>
            <div>
              <Label>Username</Label>
              <Input
                value={setupData.username}
                onChange={(e) => setSetupData({ ...setupData, username: e.target.value })}
                placeholder="admin"
                onKeyDown={(e) => e.key === 'Enter' && handleSetup()}
              />
              <p className="text-xs text-gray-400 mt-1">Minimo 3 caratteri</p>
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={setupData.password}
                onChange={(e) => setSetupData({ ...setupData, password: e.target.value })}
                placeholder="••••••••"
                onKeyDown={(e) => e.key === 'Enter' && handleSetup()}
              />
              <p className="text-xs text-gray-400 mt-1">Minimo 8 caratteri</p>
            </div>
            <div>
              <Label>Conferma Password</Label>
              <Input
                type="password"
                value={setupData.confirmPassword}
                onChange={(e) => setSetupData({ ...setupData, confirmPassword: e.target.value })}
                placeholder="••••••••"
                onKeyDown={(e) => e.key === 'Enter' && handleSetup()}
              />
            </div>
            <Button className="w-full bg-green-600 hover:bg-green-700" onClick={handleSetup} disabled={saving}>
              {saving ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Crea Super Admin
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show login form if not authenticated
  if (showLoginForm && !isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Admin Panel SaaS</CardTitle>
            <CardDescription>
              Accedi come Super Admin per gestire tenant e utenti
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loginError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                {loginError}
              </div>
            )}
            <div>
              <Label>Username</Label>
              <Input
                value={loginData.username}
                onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                placeholder="Username"
                onKeyDown={(e) => e.key === 'Enter' && handleSaaSLogin()}
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                placeholder="••••••••"
                onKeyDown={(e) => e.key === 'Enter' && handleSaaSLogin()}
              />
            </div>
            <Button className="w-full" onClick={handleSaaSLogin} disabled={saving}>
              {saving ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Shield className="h-4 w-4 mr-2" />
              )}
              Accedi come Super Admin
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Panel SaaS</h1>
          <p className="text-gray-500">Gestione tenant, utenti e abbonamenti</p>
        </div>
        <Button onClick={() => { loadStats(); loadTenants(); loadUsers(); }} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Aggiorna
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-grid">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Dashboard</span>
          </TabsTrigger>
          <TabsTrigger value="tenants" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Tenant</span>
          </TabsTrigger>
          <TabsTrigger value="cassetto" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Cassetto Fiscale</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Utenti</span>
          </TabsTrigger>
          <TabsTrigger value="plans" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Piani</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">Notifiche</span>
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Tenant Totali" 
              value={stats?.overview?.total_tenants || 0}
              icon={Building2}
              color="blue"
            />
            <StatCard 
              title="Utenti Totali" 
              value={stats?.overview?.total_users || 0}
              icon={Users}
              color="green"
            />
            <StatCard 
              title="Nuovi questo mese" 
              value={stats?.overview?.new_tenants_this_month || 0}
              icon={TrendingUp}
              color="purple"
            />
            <StatCard 
              title="Revenue Stimata" 
              value={`€${(stats?.overview?.monthly_revenue_estimate || 0).toFixed(0)}/mese`}
              icon={DollarSign}
              color="orange"
            />
          </div>

          {/* Status Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Tenant per Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats?.tenants_by_status || {}).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <StatusBadge status={status} />
                      </div>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Tenant per Piano</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats?.tenants_by_plan || {}).map(([plan, count]) => (
                    <div key={plan} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <PlanBadge plan={plan} />
                        <span className="text-gray-500 text-sm">
                          €{stats?.plans?.[plan]?.price_monthly || 0}/mese
                        </span>
                      </div>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Reset Production Section */}
          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-lg text-red-700 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Reset Produzione
              </CardTitle>
              <CardDescription className="text-red-600">
                Elimina tutti i tenant, utenti e database per ricominciare da zero
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                variant="destructive" 
                onClick={() => setShowResetModal(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Reset Completo Database
              </Button>
            </CardContent>
          </Card>

          {/* Reset Modal */}
          {showResetModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <Card className="w-full max-w-md mx-4">
                <CardHeader>
                  <CardTitle className="text-red-600 flex items-center gap-2">
                    <AlertTriangle className="h-6 w-6" />
                    Conferma Reset Completo
                  </CardTitle>
                  <CardDescription>
                    Questa operazione è <strong>IRREVERSIBILE</strong>!
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-3 bg-red-100 border border-red-300 rounded-lg text-red-800 text-sm">
                    <p className="font-semibold mb-2">Verranno eliminati:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Tutti i tenant</li>
                      <li>Tutti gli utenti tenant</li>
                      <li>Tutti gli utenti SaaS (incluso te stesso)</li>
                      <li>Tutti i database dei tenant</li>
                    </ul>
                  </div>
                  <div>
                    <Label>Password di conferma</Label>
                    <Input
                      type="password"
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      placeholder="Inserisci la password"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Password: <code className="bg-gray-100 px-1 rounded">RESET_PRODUZIONE_2026</code>
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => { setShowResetModal(false); setResetPassword(''); }}
                      className="flex-1"
                    >
                      Annulla
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={handleFullReset}
                      disabled={resettingProduction}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      {resettingProduction ? (
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 mr-2" />
                      )}
                      Conferma Reset
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Tenants Tab */}
        <TabsContent value="tenants" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Cerca tenant..."
                  value={searchTenant}
                  onChange={(e) => setSearchTenant(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="active">Attivi</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="suspended">Sospesi</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPlan} onValueChange={setFilterPlan}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Piano" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setShowCreateTenant(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Tenant
            </Button>
          </div>

          {/* Tenants List */}
          <div className="grid gap-4">
            {tenants.map((tenant) => (
              <Card key={tenant.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                        {tenant.nome?.charAt(0)?.toUpperCase() || 'T'}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{tenant.nome}</h3>
                        <p className="text-sm text-gray-500">{tenant.email}</p>
                        {tenant.ragione_sociale && (
                          <p className="text-xs text-gray-400">{tenant.ragione_sociale}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right hidden md:block">
                        <div className="flex items-center gap-2 justify-end mb-1">
                          <StatusBadge status={tenant.status} />
                          <PlanBadge plan={tenant.plan} />
                        </div>
                        {tenant.trial_ends_at && tenant.status === 'trial' && (
                          <p className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Trial fino al {new Date(tenant.trial_ends_at).toLocaleDateString('it-IT')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`view-tenant-${tenant.id}`}
                          onClick={() => setShowTenantDetail(tenant)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant={tenant.status === 'suspended' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleToggleTenantStatus(tenant)}
                          className={tenant.status === 'suspended' ? 'bg-green-600 hover:bg-green-700' : ''}
                        >
                          {tenant.status === 'suspended' ? (
                            <Play className="h-4 w-4" />
                          ) : (
                            <Pause className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {tenants.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Nessun tenant trovato</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Cassetto Fiscale Tab */}
        <TabsContent value="cassetto" className="space-y-4">
          {/* Header */}
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 min-w-[250px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Cerca cliente per nome o CF/P.IVA..."
                  value={cassettoSearchTerm}
                  onChange={(e) => setCassettoSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Badge className={cassettoStatus?.status === 'connected' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                FiscoAPI {cassettoStatus?.status === 'connected' ? 'Connesso' : 'Disconnesso'}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  loadFiscoApiSettings();
                  setShowFiscoApiSettings(true);
                }}
              >
                <Key className="h-4 w-4 mr-2" />
                Config API
              </Button>
              <Button onClick={() => setShowAddCassettoClient(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi Cliente
              </Button>
            </div>
          </div>

          {/* Info Box */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800">Come funziona il Cassetto Fiscale</p>
                  <ol className="text-sm text-blue-700 mt-1 space-y-1">
                    <li>1. Aggiungi cliente con CF/P.IVA</li>
                    <li>2. Genera Link SPID e invialo al cliente</li>
                    <li>3. Il cliente autorizza con SPID (1 minuto)</li>
                    <li>4. Scarica automaticamente le fatture!</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Clients List */}
          {cassettoLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : filteredCassettoClients.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>{cassettoSearchTerm ? 'Nessun cliente trovato' : 'Nessun cliente registrato'}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {filteredCassettoClients.map((client) => (
                <Card key={client.id}>
                  <CardContent className="py-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      {/* Info */}
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-amber-100">
                          {client.tipo === 'persona_legale' ? (
                            <Briefcase className="h-5 w-5 text-amber-600" />
                          ) : (
                            <User className="h-5 w-5 text-amber-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold">{client.nome}</h3>
                          <p className="text-sm text-gray-500">
                            {client.tipo === 'persona_legale' ? 'P.IVA' : 'CF'}: {client.codice_fiscale}
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            {getCassettoStatoBadge(client.stato)}
                            {client.fatture_count > 0 && (
                              <span className="text-xs text-gray-500">
                                <FileText className="h-3 w-3 inline mr-1" />
                                {client.fatture_count} fatture
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2">
                        {client.stato === 'non_autorizzato' && (
                          <Button
                            size="sm"
                            onClick={() => handleGenerateCassettoLink(client)}
                            disabled={cassettoActionLoading === `link-${client.id}`}
                          >
                            {cassettoActionLoading === `link-${client.id}` ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <Send className="h-4 w-4 mr-1" />
                            )}
                            Genera Link SPID
                          </Button>
                        )}

                        {client.stato === 'in_attesa' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(client.link_url);
                                toast.success('Link copiato!');
                              }}
                            >
                              <Copy className="h-4 w-4 mr-1" />
                              Copia Link
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCheckCassettoStatus(client)}
                              disabled={cassettoActionLoading === `check-${client.id}`}
                            >
                              {cassettoActionLoading === `check-${client.id}` ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              ) : (
                                <RefreshCw className="h-4 w-4 mr-1" />
                              )}
                              Verifica
                            </Button>
                          </>
                        )}

                        {client.stato === 'autorizzato' && (
                          <Button
                            size="sm"
                            onClick={() => handleDownloadCassettoFatture(client)}
                            disabled={cassettoActionLoading === `download-${client.id}`}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {cassettoActionLoading === `download-${client.id}` ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <Download className="h-4 w-4 mr-1" />
                            )}
                            Scarica Fatture
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteCassettoClient(client)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          {/* Search and Create */}
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Cerca utenti..."
                  value={searchUser}
                  onChange={(e) => setSearchUser(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Button onClick={() => setShowCreateUser(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Nuovo Utente
            </Button>
          </div>

          {/* Users List */}
          <div className="grid gap-4">
            {users
              .filter(u => !searchUser || 
                u.username?.toLowerCase().includes(searchUser.toLowerCase()) ||
                u.email?.toLowerCase().includes(searchUser.toLowerCase())
              )
              .map((user) => (
              <Card key={user.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg ${
                        user.is_super_admin 
                          ? 'bg-gradient-to-br from-red-500 to-red-600' 
                          : 'bg-gradient-to-br from-gray-500 to-gray-600'
                      }`}>
                        {user.is_super_admin ? <Shield className="h-6 w-6" /> : user.username?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{user.username}</h3>
                          {user.is_super_admin && <RoleBadge role="super_admin" />}
                        </div>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        {user.nome && <p className="text-xs text-gray-400">{user.nome} {user.cognome}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right hidden md:block">
                        <p className="text-xs text-gray-400 mb-1">
                          Accesso a {user.tenant_access?.length || 0} tenant
                        </p>
                        <div className="flex flex-wrap gap-1 justify-end">
                          {user.tenant_access?.slice(0, 2).map((access, idx) => {
                            const tenant = tenants.find(t => t.id === access.tenant_id);
                            return (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {tenant?.nome?.substring(0, 15) || access.tenant_id.substring(0, 8)}
                              </Badge>
                            );
                          })}
                          {(user.tenant_access?.length || 0) > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{user.tenant_access.length - 2}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowUserDetail(user)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {!user.is_super_admin && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowAddTenantAccess(user)}
                              title="Aggiungi accesso tenant"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleImpersonate(user.id)}
                              title="Impersona utente"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setShowResetPassword({ user, type: 'user' });
                                setNewPassword('');
                              }}
                              title="Reset Password"
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {users.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Nessun utente trovato</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Gestione Piani</h3>
            <Button onClick={() => setShowEditPlans(true)} variant="outline" size="sm">
              <Edit className="h-4 w-4 mr-2" />
              Modifica Piani
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <Card key={plan.id} className={`relative ${plan.popular ? 'ring-2 ring-cyan-500' : ''}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-cyan-500">Più popolare</Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <div className="mt-2">
                    <span className="text-4xl font-bold">€{plan.price}</span>
                    <span className="text-gray-500">/{plan.period || 'mese'}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    {plan.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 text-center text-sm text-gray-500">
                    {stats?.tenants_by_plan?.[plan.id] || 0} tenant attivi
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notifiche Push per Tenant
              </CardTitle>
              <CardDescription>
                Invia notifiche push agli utenti di un tenant specifico o a tutti
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status OneSignal */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${
                    notificationStatusLoading ? 'bg-yellow-500 animate-pulse' :
                    notificationStatus?.configured ? 'bg-green-500' : 'bg-red-500'
                  }`}></div>
                  <div>
                    <div className="font-medium">Stato OneSignal</div>
                    <div className="text-sm text-gray-500">
                      {notificationStatusLoading ? 'Verifica in corso...' : (notificationStatus?.message || 'Non configurato')}
                    </div>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('saas_token');
                      const res = await axios.get(`${API}/saas/notifications/status`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setNotificationStatus(res.data);
                      if (res.data.configured) {
                        toast.success('OneSignal configurato correttamente');
                      } else {
                        toast.warning('OneSignal non configurato');
                      }
                    } catch (e) {
                      toast.error('Errore verifica stato');
                    }
                  }}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Verifica
                </Button>
              </div>

              {/* Form Test Notifica */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="notif-tenant">Destinatario</Label>
                  <select
                    id="notif-tenant"
                    className="w-full mt-1 p-2 border rounded-md"
                    value={selectedNotifTenant || ''}
                    onChange={(e) => setSelectedNotifTenant(e.target.value)}
                  >
                    <option value="">Tutti i tenant</option>
                    {tenants.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.nome || t.name || t.id}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Seleziona un tenant per inviare solo ai suoi utenti
                  </p>
                </div>
                <div>
                  <Label htmlFor="notif-title">Titolo Notifica</Label>
                  <Input
                    id="notif-title"
                    value={testNotifTitle}
                    onChange={(e) => setTestNotifTitle(e.target.value)}
                    placeholder="Titolo della notifica"
                  />
                </div>
                <div>
                  <Label htmlFor="notif-message">Messaggio</Label>
                  <Input
                    id="notif-message"
                    value={testNotifMessage}
                    onChange={(e) => setTestNotifMessage(e.target.value)}
                    placeholder="Testo della notifica"
                  />
                </div>
                <Button 
                  className="w-full"
                  disabled={sendingNotification || !testNotifTitle || !testNotifMessage}
                  onClick={async () => {
                    try {
                      setSendingNotification(true);
                      const token = localStorage.getItem('saas_token');
                      const res = await axios.post(`${API}/saas/notifications/test`, {
                        title: testNotifTitle,
                        message: testNotifMessage,
                        tenant_id: selectedNotifTenant || null,
                        segment: selectedNotifTenant ? null : 'All'
                      }, {
                        headers: { 
                          Authorization: `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        }
                      });
                      
                      if (res.data.success) {
                        const dest = selectedNotifTenant ? 
                          tenants.find(t => t.id === selectedNotifTenant)?.nome || selectedNotifTenant : 
                          'tutti';
                        toast.success(`Notifica inviata a ${dest}!`);
                      } else {
                        toast.error(res.data.error || 'Errore invio notifica');
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.error || 'Errore invio notifica');
                    } finally {
                      setSendingNotification(false);
                    }
                  }}
                  data-testid="send-test-notification"
                >
                  {sendingNotification ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Invia Notifica
                    </>
                  )}
                </Button>
              </div>

              {/* Info */}
              <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
                <div className="font-medium mb-1">Come funziona:</div>
                <ul className="list-disc ml-4 space-y-1">
                  <li><strong>Tenant specifico:</strong> La notifica arriva solo agli utenti di quel tenant</li>
                  <li><strong>Tutti i tenant:</strong> La notifica arriva a tutti gli utenti registrati</li>
                  <li>Gli utenti devono aver accettato le notifiche push nel browser/PWA</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Cassetto Fiscale Client Modal */}
      {showAddCassettoClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Aggiungi Cliente Cassetto Fiscale</CardTitle>
              <CardDescription>Inserisci i dati del cliente per scaricare le sue fatture</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Nome / Ragione Sociale *</Label>
                <Input
                  value={newCassettoClient.nome}
                  onChange={(e) => setNewCassettoClient({ ...newCassettoClient, nome: e.target.value })}
                  placeholder="Es. Ristorante da Mario"
                />
              </div>
              <div>
                <Label>Codice Fiscale / P.IVA *</Label>
                <Input
                  value={newCassettoClient.codice_fiscale}
                  onChange={(e) => setNewCassettoClient({ ...newCassettoClient, codice_fiscale: e.target.value.toUpperCase().replace(/^IT/, '') })}
                  placeholder="Es. 12345678901 (senza IT)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Inserisci solo le cifre, senza prefisso "IT"
                </p>
              </div>
              <div>
                <Label>Tipo</Label>
                <Select 
                  value={newCassettoClient.tipo} 
                  onValueChange={(v) => setNewCassettoClient({ ...newCassettoClient, tipo: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="persona_legale">Persona Giuridica (Azienda)</SelectItem>
                    <SelectItem value="persona_fisica">Persona Fisica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tenant di Destinazione (Database Fatture) *</Label>
                <Select 
                  value={newCassettoClient.tenant_db_target} 
                  onValueChange={(v) => setNewCassettoClient({ ...newCassettoClient, tenant_db_target: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona il tenant..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.map(t => (
                      <SelectItem key={t.id} value={t.db_name || `tenant_${t.id}`}>
                        {t.nome} ({t.db_name || `tenant_${t.id}`})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Le fatture verranno salvate nel database del tenant selezionato
                </p>
              </div>
              <div>
                <Label>Email (opzionale)</Label>
                <Input
                  type="email"
                  value={newCassettoClient.email}
                  onChange={(e) => setNewCassettoClient({ ...newCassettoClient, email: e.target.value })}
                  placeholder="email@esempio.it"
                />
              </div>
            </CardContent>
            <div className="flex justify-end gap-3 p-6 pt-0">
              <Button variant="outline" onClick={() => setShowAddCassettoClient(false)}>
                Annulla
              </Button>
              <Button 
                onClick={handleAddCassettoClient}
                disabled={cassettoActionLoading === 'add' || !newCassettoClient.tenant_db_target}
              >
                {cassettoActionLoading === 'add' ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Aggiungi
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* FiscoAPI Settings Modal */}
      {showFiscoApiSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5 text-amber-500" />
                Impostazioni FiscoAPI
              </CardTitle>
              <CardDescription>Configura le credenziali per il Cassetto Fiscale</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Username */}
              <div>
                <Label>Username FiscoAPI</Label>
                <Input
                  type="text"
                  value={fiscoApiUsername}
                  onChange={(e) => setFiscoApiUsername(e.target.value)}
                  placeholder="Inserisci il tuo username FiscoAPI"
                />
              </div>

              {/* Password */}
              <div>
                <Label>Password FiscoAPI</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={fiscoApiPassword}
                    onChange={(e) => setFiscoApiPassword(e.target.value)}
                    placeholder="Inserisci la tua password FiscoAPI"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <X className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Chiave Segreta API */}
              <div>
                <Label>Chiave Segreta API</Label>
                <div className="relative">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={fiscoApiKey}
                    onChange={(e) => setFiscoApiKey(e.target.value)}
                    placeholder="Inserisci la tua chiave segreta FiscoAPI"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showApiKey ? <X className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Puoi trovare le credenziali nel portale FiscoAPI → Impostazioni
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  <strong>Nota:</strong> Modifica solo i campi che vuoi aggiornare. I campi mascherati (****) non verranno modificati.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-700">
                  <Shield className="h-4 w-4 inline mr-1" />
                  <strong>Dove vanno le fatture?</strong> Le fatture vengono salvate nel database del tenant di riferimento associato al cliente fiscale.
                </p>
              </div>
            </CardContent>
            <div className="flex justify-end gap-3 p-6 pt-0">
              <Button variant="outline" onClick={() => setShowFiscoApiSettings(false)}>
                Annulla
              </Button>
              <Button 
                onClick={handleSaveFiscoApiSettings}
                disabled={savingFiscoApiSettings}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {savingFiscoApiSettings ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Salva
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Create Tenant Modal */}
      {showCreateTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Nuovo Tenant</CardTitle>
              <CardDescription>Crea un nuovo ristorante/azienda</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nome *</Label>
                  <Input
                    value={newTenant.nome}
                    onChange={(e) => setNewTenant({ ...newTenant, nome: e.target.value })}
                    placeholder="Nome ristorante"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Ragione Sociale</Label>
                  <Input
                    value={newTenant.ragione_sociale}
                    onChange={(e) => setNewTenant({ ...newTenant, ragione_sociale: e.target.value })}
                    placeholder="Ragione sociale SRL"
                  />
                </div>
                <div>
                  <Label>Partita IVA</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newTenant.partita_iva}
                      onChange={(e) => setNewTenant({ ...newTenant, partita_iva: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                      placeholder="12345678901"
                      maxLength={11}
                      className="flex-1"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="icon"
                      onClick={handleSearchPiva}
                      disabled={searchingPiva || newTenant.partita_iva.length < 11}
                      title="Cerca dati societari"
                    >
                      {searchingPiva ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Inserisci la P.IVA e clicca 🔍 per compilare automaticamente</p>
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newTenant.email}
                    onChange={(e) => setNewTenant({ ...newTenant, email: e.target.value })}
                    placeholder="info@ristorante.it"
                  />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input
                    value={newTenant.telefono}
                    onChange={(e) => setNewTenant({ ...newTenant, telefono: e.target.value })}
                    placeholder="02 1234567"
                  />
                </div>
                <div>
                  <Label>Indirizzo</Label>
                  <Input
                    value={newTenant.indirizzo}
                    onChange={(e) => setNewTenant({ ...newTenant, indirizzo: e.target.value })}
                    placeholder="Via Roma 1"
                  />
                </div>
                <div>
                  <Label>CAP</Label>
                  <Input
                    value={newTenant.cap}
                    onChange={(e) => setNewTenant({ ...newTenant, cap: e.target.value })}
                    placeholder="00100"
                  />
                </div>
                <div>
                  <Label>Città</Label>
                  <Input
                    value={newTenant.citta}
                    onChange={(e) => setNewTenant({ ...newTenant, citta: e.target.value })}
                    placeholder="Roma"
                  />
                </div>
                <div>
                  <Label>Provincia</Label>
                  <Input
                    value={newTenant.provincia}
                    onChange={(e) => setNewTenant({ ...newTenant, provincia: e.target.value })}
                    placeholder="RM"
                    maxLength={2}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCreateTenant(false)}>
                  Annulla
                </Button>
                <Button onClick={handleCreateTenant} disabled={saving}>
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Crea Tenant
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Nuovo Utente</CardTitle>
              <CardDescription>Crea un nuovo utente nel sistema</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Username *</Label>
                  <Input
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    placeholder="mario.rossi"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="mario@email.it"
                  />
                </div>
                <div>
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Min. 8 caratteri"
                  />
                </div>
                <div>
                  <Label>Nome</Label>
                  <Input
                    value={newUser.nome}
                    onChange={(e) => setNewUser({ ...newUser, nome: e.target.value })}
                    placeholder="Mario"
                  />
                </div>
                <div>
                  <Label>Cognome</Label>
                  <Input
                    value={newUser.cognome}
                    onChange={(e) => setNewUser({ ...newUser, cognome: e.target.value })}
                    placeholder="Rossi"
                  />
                </div>
                <div>
                  <Label>Assegna a Tenant</Label>
                  <Select value={newUser.tenant_id || 'none'} onValueChange={(v) => setNewUser({ ...newUser, tenant_id: v === 'none' ? '' : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Nessuno" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuno</SelectItem>
                      {tenants.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {newUser.tenant_id && (
                  <div>
                    <Label>Ruolo</Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="operator">Operatore</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCreateUser(false)}>
                  Annulla
                </Button>
                <Button onClick={handleCreateUser} disabled={saving}>
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                  Crea Utente
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tenant Detail/Edit Modal */}
      {showTenantDetail && (
        <TenantDetailModal 
          tenant={showTenantDetail}
          onClose={() => setShowTenantDetail(null)}
          onUpdate={() => { loadTenants(); setShowTenantDetail(null); }}
          saasToken={getToken()}
        />
      )}

      {/* User Detail Modal */}
      {showUserDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold ${
                    showUserDetail.is_super_admin ? 'bg-red-500' : 'bg-gray-500'
                  }`}>
                    {showUserDetail.is_super_admin ? <Shield className="h-6 w-6" /> : showUserDetail.username?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <CardTitle>{showUserDetail.username}</CardTitle>
                    <CardDescription>{showUserDetail.email}</CardDescription>
                  </div>
                </div>
                {showUserDetail.is_super_admin && <RoleBadge role="super_admin" />}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Nome</p>
                  <p className="font-medium">{showUserDetail.nome || '-'} {showUserDetail.cognome || ''}</p>
                </div>
                <div>
                  <p className="text-gray-500">Stato</p>
                  <Badge className={showUserDetail.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                    {showUserDetail.is_active ? 'Attivo' : 'Disattivato'}
                  </Badge>
                </div>
                <div>
                  <p className="text-gray-500">Ultimo accesso</p>
                  <p className="font-medium">
                    {showUserDetail.last_login 
                      ? new Date(showUserDetail.last_login).toLocaleString('it-IT')
                      : 'Mai'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Registrato il</p>
                  <p className="font-medium">{new Date(showUserDetail.created_at).toLocaleDateString('it-IT')}</p>
                </div>
              </div>
              
              {/* Tenant Access */}
              <div>
                <h4 className="font-medium mb-2">Accesso ai Tenant</h4>
                {showUserDetail.tenant_access?.length > 0 ? (
                  <div className="space-y-2">
                    {showUserDetail.tenant_access.map((access, idx) => {
                      const tenant = tenants.find(t => t.id === access.tenant_id);
                      return (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            <span>{tenant?.nome || access.tenant_id}</span>
                            <RoleBadge role={access.role} />
                            {access.is_default && <Badge variant="outline" className="text-xs">Default</Badge>}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleRemoveTenantAccess(showUserDetail.id, access.tenant_id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Nessun accesso a tenant</p>
                )}
              </div>
              
              <div className="flex justify-end pt-4">
                <Button variant="outline" onClick={() => setShowUserDetail(null)}>
                  Chiudi
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Tenant Access Modal */}
      {showAddTenantAccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Aggiungi Accesso Tenant</CardTitle>
              <CardDescription>
                Aggiungi accesso per {showAddTenantAccess.username}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Seleziona Tenant</Label>
                  <Select onValueChange={(tenantId) => {
                    const roleSelect = document.getElementById('role-select');
                    const role = roleSelect?.value || 'operator';
                    handleAddTenantAccess(showAddTenantAccess.id, tenantId, role);
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Scegli un tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants
                        .filter(t => !showAddTenantAccess.tenant_access?.some(a => a.tenant_id === t.id))
                        .map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Ruolo</Label>
                  <Select defaultValue="operator" id="role-select">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="operator">Operatore</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end pt-4">
                <Button variant="outline" onClick={() => setShowAddTenantAccess(null)}>
                  Annulla
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Plans Modal */}
      {showEditPlans && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Modifica Piani</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowEditPlans(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-500">Modifica i piani abbonamento mostrati sulla landing page</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {(editingPlans.length > 0 ? editingPlans : plans).map((plan, planIdx) => (
                <div key={plan.id} className={`p-4 border rounded-lg ${plan.popular ? 'border-cyan-500 bg-cyan-50/50' : ''}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-lg">{plan.name}</h4>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={plan.popular || false}
                        onChange={(e) => {
                          const newPlans = [...(editingPlans.length > 0 ? editingPlans : plans)];
                          // Rimuovi popular da tutti gli altri
                          newPlans.forEach((p, i) => {
                            newPlans[i] = { ...p, popular: i === planIdx ? e.target.checked : false };
                          });
                          setEditingPlans(newPlans);
                        }}
                        className="rounded"
                      />
                      Più popolare
                    </label>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <Label className="text-sm">Nome Piano</Label>
                      <Input
                        value={plan.name}
                        onChange={(e) => {
                          const newPlans = [...(editingPlans.length > 0 ? editingPlans : plans)];
                          newPlans[planIdx] = { ...newPlans[planIdx], name: e.target.value };
                          setEditingPlans(newPlans);
                        }}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Prezzo (€)</Label>
                      <Input
                        value={plan.price}
                        onChange={(e) => {
                          const newPlans = [...(editingPlans.length > 0 ? editingPlans : plans)];
                          newPlans[planIdx] = { ...newPlans[planIdx], price: e.target.value };
                          setEditingPlans(newPlans);
                        }}
                        className="mt-1"
                        placeholder="79"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Periodo</Label>
                      <Select
                        value={plan.period || 'mese'}
                        onValueChange={(v) => {
                          const newPlans = [...(editingPlans.length > 0 ? editingPlans : plans)];
                          newPlans[planIdx] = { ...newPlans[planIdx], period: v };
                          setEditingPlans(newPlans);
                        }}
                      >
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mese">mese</SelectItem>
                          <SelectItem value="anno">anno</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Funzionalità (una per riga)</Label>
                    <Textarea
                      value={(plan.features || []).join('\n')}
                      onChange={(e) => {
                        const newPlans = [...(editingPlans.length > 0 ? editingPlans : plans)];
                        newPlans[planIdx] = { 
                          ...newPlans[planIdx], 
                          features: e.target.value.split('\n').filter(f => f.trim()) 
                        };
                        setEditingPlans(newPlans);
                      }}
                      className="mt-1 font-mono text-sm"
                      rows={5}
                      placeholder="5 utenti&#10;1.000 prodotti&#10;Supporto email"
                    />
                  </div>
                </div>
              ))}
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => {
                  setEditingPlans([]);
                  setShowEditPlans(false);
                }}>
                  Annulla
                </Button>
                <Button 
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const token = getToken();
                      const plansToSave = editingPlans.length > 0 ? editingPlans : plans;
                      await axios.put(`${API.replace('/saas', '')}/plans`, plansToSave, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      toast.success('Piani aggiornati!');
                      setPlans(plansToSave);
                      setEditingPlans([]);
                      setShowEditPlans(false);
                    } catch (error) {
                      toast.error('Errore nel salvataggio');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                >
                  {saving ? 'Salvataggio...' : 'Salva Piani'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Reset Password
              </CardTitle>
              <CardDescription>
                Imposta una nuova password per <strong>{showResetPassword.user.username}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Nuova Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimo 6 caratteri"
                  data-testid="new-password-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  La password deve essere di almeno 6 caratteri
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowResetPassword(null);
                    setNewPassword('');
                  }}
                >
                  Annulla
                </Button>
                <Button 
                  onClick={handleResetPassword} 
                  disabled={resettingPassword || newPassword.length < 6}
                  data-testid="confirm-reset-password"
                >
                  {resettingPassword ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      Reset in corso...
                    </>
                  ) : (
                    <>
                      <Key className="h-4 w-4 mr-2" />
                      Reset Password
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SaaSAdminPage;
